/**
 * CR-SEQ-2026-025 — one cell formatter for live previews, snapshots and CSV.
 *
 * IMPORTANT: this must only ever use the stored column definition — never the
 * live catalogue. Snapshots carry their column definitions with them, and a
 * reopened snapshot has to render from that stored payload alone, so that
 * later catalogue changes cannot rewrite the historical view.
 */
import { t } from "i18next";

import { ReportColumn } from "@services/reportEngine";
import { formatDate } from "@utils/formatDate";
import { convertMLToLiter } from "@utils/convertUnits";
import { formatVolume } from "@utils/formatVolume";
import {
  formatNotesForCsv,
  ReportNoteRow,
} from "@components/notes/reportNotes";

/**
 * Date columns arrive from the engine as plain "yyyy-MM-dd" text. The time is
 * appended so they parse in the local zone — the same thing the report date
 * pickers do — because `new Date("2026-01-01")` is UTC midnight and can print
 * the day before. formatDate itself only accepts a Date.
 */
const parseReportDate = (value: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);

/**
 * A report period is a calendar date too: the engine applied those days in the
 * scheme's timezone, but the API returns them as timestamps. The date part is
 * taken rather than re-zoned, otherwise anyone whose clock sits west of the
 * scheme reads the period as starting a day early.
 */
export const formatReportPeriodDate = (isoDate: string) =>
  formatDate(parseReportDate(isoDate.slice(0, 10)));

// Keep filter definitions in raw units, including saved templates. Only the
// editor and summaries use ML, matching the report's volume columns.
export const reportFilterInputValue = (
  value: string | number,
  dataType?: ReportColumn["dataType"],
) =>
  dataType === "volume" && value !== "" ? Number(value) / 1_000_000 : value;

export const reportFilterStoredValue = (
  value: string,
  dataType?: ReportColumn["dataType"],
) =>
  dataType === "volume" && value !== ""
    ? String(convertMLToLiter(Number(value)))
    : value;

export const formatReportFilterValue = (
  value: string | number,
  dataType?: ReportColumn["dataType"],
) =>
  `${reportFilterInputValue(value, dataType)}${
    dataType === "volume" ? " ML" : ""
  }`;

// History search uses Queensland calendar days, regardless of browser zone.
export const formatReportGeneratedAt = (isoDate: string) =>
  new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(isoDate));

export const formatCell = (
  value: any,
  dataType: ReportColumn["dataType"],
  columnName?: ReportColumn["name"],
): string => {
  if (value === null || value === undefined || value === "") return "-";

  switch (dataType) {
    case "volume":
      return formatVolume(Number(value));
    case "percent":
      return `${Number(value)}%`;
    case "number":
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: columnName === "usageCost" ? 2 : 0,
        maximumFractionDigits: columnName === "usageCost" ? 2 : 3,
      });
    case "date":
      return formatDate(parseReportDate(String(value)));
    case "boolean":
      return value ? t("common.yes") : t("common.no");
    case "notes":
      return Array.isArray(value)
        ? formatNotesForCsv(value as ReportNoteRow[])
        : "-";
    default:
      return String(value);
  }
};

/**
 * CSV values: display labels for headings, date-safe values so spreadsheets
 * do not reinterpret them (CR §1.3.7). Dates are wrapped the same way the
 * legacy export does.
 */
export const formatCsvValue = (
  value: any,
  dataType: ReportColumn["dataType"],
  columnName?: ReportColumn["name"],
): string => {
  if (value === null || value === undefined) return "";
  if (dataType === "date")
    return `="${formatDate(parseReportDate(String(value)))}"`;

  const formatted = formatCell(value, dataType, columnName);
  // Spreadsheet applications may execute a quoted CSV cell beginning with
  // one of these characters as a formula. Customer names and identifiers are
  // user-controlled, so force them to text.
  if (
    ["string", "notes"].includes(dataType) &&
    /^[=+\-@\t\r]/.test(formatted)
  ) {
    return `'${formatted}`;
  }
  return formatted;
};
